<!-- eslint-disable vue/first-attribute-linebreak -->
<!-- "ตั๋วที่ครบกำหนดลงเอยอย่างไร" — the ticket-cohort view of the Customer Behavior page. Unit = one
     ticket, grouped by the month it falls due (rolled up to the page's month / quarter / year view),
     so tickets nobody came back for (forfeited / still waiting) are counted too. Data: the
     /insight/due-cohort response, passed in by CustomerBehavior.vue. -->
<template>
  <div class="row mb-4">
    <div class="col-12">
      <div class="card">
        <div class="card-header pb-0 cbc-head">
          <div>
            <h6 class="mb-0">ตั๋วที่ครบกำหนดลงเอยอย่างไร</h6>
            <p class="text-sm mb-0">นับตั๋ว 1 ใบเป็น 1 หน่วย จัดกลุ่มตามเดือนที่ตั๋วครบกำหนด · รวมตั๋วที่หลุดและตั๋วที่ยังไม่มีคนมา</p>
          </div>
          <span class="cbc-badge">ทุกประเภทธุรกรรม</span>
        </div>
        <div class="card-body pt-3">
          <p v-if="error" class="text-sm text-danger mb-0">{{ error }}</p>
          <p v-else-if="!rows.length" class="text-sm mb-0">ยังไม่มีข้อมูลตั๋ว</p>
          <template v-else>
            <div class="cbc-legend">
              <span v-for="g in GROUPS" :key="g.name"><span class="cbc-sw" :style="{ background: g.color }"></span>{{ g.name }}</span>
            </div>

            <!-- A vs B -->
            <div class="cbc-context">
              <span><span class="cbc-tag a" aria-hidden="true">A</span><b>ตั๋วที่ครบกำหนดใน:</b> {{ label(periodA, true) }}</span>
              <span><span class="cbc-tag b" aria-hidden="true">B</span><b>เทียบกับ:</b> {{ periodB ? label(periodB, true) : "ไม่ได้เลือกช่วงเทียบ" }}</span>
              <span class="text-secondary">ผลต่าง = % ของ A ลบ % ของ B ตรงๆ</span>
            </div>
            <div class="table-responsive cbc-scroll">
              <table class="table mb-0 cbc-table">
                <thead>
                  <tr>
                    <th scope="col">ตั๋วลงเอยอย่างไร</th>
                    <th scope="col"><span class="th-main">ช่วงหลัก A</span><span class="th-sub">{{ label(periodA, true) }}</span></th>
                    <th scope="col"><span class="th-main">ช่วงเทียบ B</span><span class="th-sub">{{ periodB ? label(periodB, true) : "—" }}</span></th>
                    <th scope="col"><span class="th-main">ผลต่าง</span><span class="th-sub">% ของ A ลบ % ของ B</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in compare.rows" v-show="r.cls !== 'age' || openAge.compare" :key="r.name" :class="r.cls"
                    @click="r.cls === 'early' && (openAge.compare = !openAge.compare)">
                    <th scope="row">
                      <span class="cbc-rowlabel">
                        <button v-if="r.cls === 'early'" type="button" class="cbc-row-toggle" :aria-expanded="openAge.compare"
                          :aria-label="`${openAge.compare ? 'ซ่อน' : 'แสดง'}ช่วงอายุตั๋ว`" @click.stop="openAge.compare = !openAge.compare">
                          <i class="material-icons material-symbols-outlined" aria-hidden="true">chevron_right</i>
                        </button>
                        <span v-if="r.color" class="cbc-sw" :style="{ background: r.color }"></span>{{ r.name }}
                        <span v-if="r.cls === 'early'" class="cbc-row-hint">{{ openAge.compare ? "ซ่อนรายละเอียด" : "ดูตามอายุตั๋ว" }}</span>
                      </span>
                    </th>
                    <td>{{ p2(r.pa) }}<small>{{ fmt(r.na) }} ใบ</small></td>
                    <td class="b">{{ r.pb === null ? "—" : p2(r.pb) }}<small v-if="r.pb !== null">{{ fmt(r.nb) }} ใบ</small></td>
                    <td class="d" :class="{ flat: r.pb === null || Math.abs(r.pa - r.pb) < 0.005 }">{{ r.pb === null ? "—" : diff(r.pa, r.pb) }}</td>
                  </tr>
                  <tr class="total">
                    <th scope="row">ตั๋วที่ครบกำหนดทั้งหมด (100%)</th>
                    <td>{{ fmt(compare.nA) }} ใบ</td>
                    <td class="b">{{ periodB ? fmt(compare.nB) + " ใบ" : "—" }}</td>
                    <td class="d">{{ compare.chg === null ? "—" : pctChange(compare.chg) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="settleNote" class="cbc-flag">
              <i class="material-icons material-symbols-outlined" aria-hidden="true">info</i>
              <div>{{ settleNote }}</div>
            </div>

            <!-- next transaction of the customers who came back, period A -->
            <h6 class="cbc-subtitle">ลูกค้าที่มาแล้ว ทำรายการอะไรต่อ · {{ label(periodA, true) }}</h6>
            <!-- the period here means "tickets due in it"; the transaction tables use "transactions made in it" -->
            <div class="cbc-explain">
              <i class="material-icons material-symbols-outlined" aria-hidden="true">help_outline</i>
              <div>
                <p class="mb-1"><b>{{ label(periodA, true) }} ในตารางนี้ = ตั๋วที่ครบกำหนดในช่วงนี้</b> ไม่ว่าลูกค้าจะมาทำรายการเมื่อไร</p>
                <p class="mb-0">ตารางนี้ตอบว่า ตั๋วที่ครบเดือนนี้ คนที่มาแล้วเลือกทำอะไรกับตั๋ว ใช้ร่วมกับตารางผลของตั๋วด้านบน (มาก่อน / ตรงวัน / หลุด …) เพื่อติดตามตั๋วในแต่ละ chain</p>
              </div>
            </div>
            <div class="table-responsive cbc-scroll">
              <table class="table mb-0 cbc-table">
                <thead>
                  <tr>
                    <th scope="col">ตั๋วลงเอยอย่างไร</th>
                    <th v-for="t in NEXT_TYPES" :key="t.v" scope="col">{{ t.lb }}</th>
                    <th scope="col">ตั๋ว</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in nextMix" v-show="r.cls !== 'age' || openAge.next" :key="r.name" :class="r.cls"
                    @click="r.cls === 'early' && (openAge.next = !openAge.next)">
                    <th scope="row">
                      <span class="cbc-rowlabel">
                        <button v-if="r.cls === 'early'" type="button" class="cbc-row-toggle" :aria-expanded="openAge.next"
                          :aria-label="`${openAge.next ? 'ซ่อน' : 'แสดง'}ช่วงอายุตั๋ว`" @click.stop="openAge.next = !openAge.next">
                          <i class="material-icons material-symbols-outlined" aria-hidden="true">chevron_right</i>
                        </button>
                        <span v-if="r.color" class="cbc-sw" :style="{ background: r.color }"></span>{{ r.name }}
                        <span v-if="r.cls === 'early'" class="cbc-row-hint">{{ openAge.next ? "ซ่อนรายละเอียด" : "ดูตามอายุตั๋ว" }}</span>
                      </span>
                    </th>
                    <td v-for="(v, i) in r.pcts" :key="i"><span class="cbc-cellbar">{{ p2(v) }}<i :style="{ width: Math.round(v * 0.5) + 'px' }"></i></span></td>
                    <td>{{ fmt(r.n) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- every due period, grouped by year -->
            <div class="cbc-periods-head">
              <h6 class="cbc-subtitle mb-0">แยกตามเดือนที่ครบกำหนด</h6>
              <div v-if="gran !== 'year'" class="cbc-seg">
                <button type="button" class="btn btn-sm btn-outline-primary mb-0" @click="openYears = new Set(years)">ขยายทุกปี</button>
                <button type="button" class="btn btn-sm btn-outline-primary mb-0" @click="openYears = new Set()">ย่อทุกปี</button>
              </div>
            </div>
            <p class="text-xs text-secondary mb-2">ใหม่สุดอยู่บน · แสดงถึงช่วงที่มีข้อมูล ({{ asOfText }}) · ตั๋วที่ครบกำหนดหลังจากนั้นยังไม่ได้แสดง</p>
            <div class="table-responsive cbc-scroll cbc-periods">
              <table class="table mb-0 cbc-table">
                <thead>
                  <tr>
                    <th scope="col">ครบกำหนด</th>
                    <th scope="col">ตั๋ว</th>
                    <th scope="col">สัดส่วน</th>
                    <th v-for="g in GROUPS" :key="g.name" scope="col"><span class="cbc-sw" :style="{ background: g.color }"></span> {{ g.short }}</th>
                    <th scope="col">หมายเหตุ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in periodRows" v-show="!r.child || openYears.has(r.year)" :key="r.key"
                    :class="{ 'year-row': r.isYear, child: r.child, 'is-a': r.key === periodA }" @click="r.isYear && toggleYear(r.year)">
                    <th scope="row">
                      <button v-if="r.isYear" type="button" class="cbc-row-toggle" :aria-expanded="openYears.has(r.year)"
                        :aria-label="`${openYears.has(r.year) ? 'ย่อ' : 'ขยาย'} ปี ${r.year}`" @click.stop="toggleYear(r.year)">
                        <i class="material-icons material-symbols-outlined" aria-hidden="true">chevron_right</i>
                      </button>
                      {{ r.name }}
                      <span v-for="tg in r.tags" :key="tg" class="cbc-tag sm" :class="tg.toLowerCase()">{{ tg }}</span>
                    </th>
                    <td>{{ fmt(r.n) }}</td>
                    <td>
                      <div class="cbc-mixbar" aria-hidden="true">
                        <span v-for="(v, j) in r.g" v-show="v > 0" :key="j" :style="{ flex: v + ' 1 0', background: GROUPS[j].color }"></span>
                      </div>
                    </td>
                    <td v-for="(v, j) in r.g" :key="'v' + j">{{ p2(pct(v, r.n)) }}<small>{{ fmt(v) }}</small></td>
                    <td class="note">{{ r.note }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";

const props = defineProps({
  cohort: { type: Object, default: null },     // /insight/due-cohort response
  error: { type: String, default: "" },
  gran: { type: String, required: true },      // month | quarter | year
  periodA: { type: String, default: null },
  periodB: { type: String, default: null },
  firstDate: { type: String, default: null },  // first transaction date in scope (flags the partial first due month)
  label: { type: Function, required: true },   // (key, long) => period label
});

// outcome codes from /insight/due-cohort: 0 before due, 1 on due, 2 late, 3 postponed then came,
// 4 forfeited, 5 past due nobody came, 6 postponed and new date ahead, 7 not due yet
const GROUPS = [
  { name: "มาก่อนกำหนด", short: "ก่อน", color: "#2563eb", codes: [0] },
  { name: "ตรงวันครบ", short: "ตรงวัน", color: "#0d9488", codes: [1] },
  { name: "เกินกำหนด (ไม่ได้ผลัด)", short: "เกิน", color: "#f59e0b", codes: [2] },
  { name: "ผลัดตั๋วแล้วมา", short: "ผลัดแล้วมา", color: "#7c3aed", codes: [3] },
  { name: "หลุด", short: "หลุด", color: "#e11d48", codes: [4] },
  { name: "ยังไม่จบ", short: "ยังไม่จบ", color: "#ced4da", codes: [5, 6, 7] },
];
const PENDING = [
  { name: "เลยกำหนดแล้ว ยังไม่มา", code: 5 },
  { name: "ผลัดไว้ ยังไม่ถึงวันครบใหม่", code: 6 },
  { name: "ยังไม่ถึงวันครบกำหนด", code: 7 },
];
// "มาก่อนกำหนด" split by ticket age when the customer came (ageBin 0..11 = 0.5..6 months, same bins as
// /insight/due-behavior); the age rows start collapsed in both tables
const AGES = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6];
const openAge = reactive({ compare: false, next: false });
const NEXT_TYPES = [
  { v: "REDEEM", lb: "ไถ่ถอน" }, { v: "INCREASE", lb: "เพิ่มต้น" }, { v: "RENEW", lb: "ต่อดอก" }, { v: "REDUCE", lb: "ลดต้น" },
];
const TH_M = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const pct = (a, b) => (b ? (a / b) * 100 : 0);
const p2 = (x) => `${x.toFixed(2)}%`;
const sum = (a) => a.reduce((x, y) => x + y, 0);
const diff = (a, b) => { const d = a - b; return Math.abs(d) < 0.005 ? "±0.00" : `${d > 0 ? "▲ +" : "▼ −"}${Math.abs(d).toFixed(2)}`; };
const pctChange = (c) => `${c >= 0 ? "▲ +" : "▼ −"}${Math.abs(c).toFixed(2)}%`;
const thDate = (s) => { const [y, m, d] = s.split("-"); return `${+d} ${TH_M[+m - 1]} ${y}`; };

function keyOf(month, g) {
  const [y, m] = month.split("-");
  if (g === "month") return month;
  if (g === "quarter") return `${y}-Q${Math.ceil(+m / 3)}`;
  return y;
}

const rows = computed(() => props.cohort?.rows || []);
const asOfMonth = computed(() => (props.cohort?.asOfDate || "").slice(0, 7));
const asOfText = computed(() => (props.cohort?.asOfDate ? `ข้อมูลถึง ${thDate(props.cohort.asOfDate)}` : ""));
// first due month only holds tickets issued from the first data day onwards
const firstDueMonth = computed(() => {
  if (!props.firstDate) return "";
  const [y, m] = props.firstDate.split("-").map(Number);
  const idx = y * 12 + (m - 1) + 6;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
});

// period key -> { o: count per outcome code (8), early: outcome-0 count per age bin (12),
//                  next: { code: { type: count } }, nextAge: { ageBin: { type: count } } }
const agg = computed(() => {
  const out = {};
  for (const r of rows.value) {
    if (r.dueMonth > asOfMonth.value) continue; // not due yet within the data window
    const k = keyOf(r.dueMonth, props.gran);
    const a = out[k] || (out[k] = { o: Array(8).fill(0), early: Array(12).fill(0), next: {}, nextAge: {}, months: new Set() });
    a.o[r.outcome] += r.count;
    a.months.add(r.dueMonth);
    const age = r.outcome === 0 && r.ageBin !== null && r.ageBin !== undefined ? r.ageBin : null;
    if (age !== null) a.early[age] += r.count;
    if (r.nextType) {
      const n = a.next[r.outcome] || (a.next[r.outcome] = {});
      n[r.nextType] = (n[r.nextType] || 0) + r.count;
      if (age !== null) {
        const na = a.nextAge[age] || (a.nextAge[age] = {});
        na[r.nextType] = (na[r.nextType] || 0) + r.count;
      }
    }
  }
  return out;
});
const groupsOf = (o) => GROUPS.map((g) => sum(g.codes.map((c) => o[c])));
const outcomesFor = (k) => (agg.value[k] ? agg.value[k].o : Array(8).fill(0));

const compare = computed(() => {
  const oA = outcomesFor(props.periodA), nA = sum(oA);
  const oB = props.periodB ? outcomesFor(props.periodB) : null, nB = oB ? sum(oB) : 0;
  const line = (name, codes, extra) => {
    const na = sum(codes.map((c) => oA[c])), nb = oB ? sum(codes.map((c) => oB[c])) : 0;
    return { name, na, nb, pa: pct(na, nA), pb: oB && nB ? pct(nb, nB) : null, ...extra };
  };
  const out = GROUPS.map((g, gi) => line(g.name, g.codes, { color: g.color, cls: gi === 0 ? "early" : g.codes.length > 1 ? "pending" : "" }));
  // age split of "มาก่อนกำหนด", as a share of all tickets so the rows add up to the early row
  const eA = agg.value[props.periodA]?.early || Array(12).fill(0);
  const eB = props.periodB ? agg.value[props.periodB]?.early || Array(12).fill(0) : null;
  out.splice(1, 0, ...AGES.map((m, i) => ({
    name: `ตั๋วอายุ ${m} เดือน`, cls: "age", na: eA[i], nb: eB ? eB[i] : 0,
    pa: pct(eA[i], nA), pb: eB && nB ? pct(eB[i], nB) : null,
  })));
  // spell out "ยังไม่จบ" when it is not empty in either period
  const pendingIdx = out.length - 1;
  if (out[pendingIdx].na || out[pendingIdx].nb) {
    out.push(...PENDING.map((p) => line(`· ${p.name}`, [p.code], { cls: "sub" })));
  }
  return { rows: out, nA, nB, chg: oB && nB ? ((nA - nB) / nB) * 100 : null };
});

const settleNote = computed(() => {
  const o = outcomesFor(props.periodA), n = sum(o);
  const pend = o[5] + o[6] + o[7];
  const bits = [];
  if (n && pct(pend, n) >= 1) bits.push(`ช่วง A ยังมีตั๋วที่ยังไม่จบ ${p2(pct(pend, n))} ตัวเลขยังขยับได้เมื่อมีข้อมูลใหม่`);
  const months = agg.value[props.periodA]?.months;
  if (months && months.has(firstDueMonth.value)) bits.push(`เดือนครบกำหนดแรกมีเฉพาะตั๋วที่ออกตั้งแต่ ${thDate(props.firstDate)}`);
  return bits.join(" · ");
});

const nextMix = computed(() => {
  const a = agg.value[props.periodA];
  const mixOf = (by) => { const n = sum(Object.values(by)); return { n, pcts: NEXT_TYPES.map((t) => pct(by[t.v] || 0, n)) }; };
  const out = GROUPS.slice(0, 4).map((g, gi) => ({
    name: g.name, color: g.color, cls: gi === 0 ? "early" : "", ...mixOf((a && a.next[g.codes[0]]) || {}),
  }));
  // each age row adds up to 100% on its own: what customers who came at that ticket age did
  out.splice(1, 0, ...AGES.map((m, i) => ({ name: `ตั๋วอายุ ${m} เดือน`, cls: "age", ...mixOf((a && a.nextAge[i]) || {}) })));
  return out;
});

// ---------- every due period, grouped by year ----------
const periodKeys = computed(() => Object.keys(agg.value).sort().reverse());
const years = computed(() => [...new Set(periodKeys.value.map((k) => k.slice(0, 4)))]);
const openYears = ref(new Set());
watch(years, (ys) => { openYears.value = new Set(ys.slice(0, 1)); }, { immediate: true });
function toggleYear(y) {
  const s = new Set(openYears.value);
  if (s.has(y)) s.delete(y); else s.add(y);
  openYears.value = s;
}
function noteOf(o, months) {
  const n = sum(o), pend = o[5] + o[6] + o[7], bits = [];
  if (months && months.has(firstDueMonth.value)) bits.push("เดือนแรกมีข้อมูลไม่เต็มเดือน");
  if (n && pct(pend, n) >= 1) bits.push(`ยังไม่จบ ${p2(pct(pend, n))}`);
  return bits.join(" · ");
}
const tagsOf = (k) => [k === props.periodA ? "A" : null, k === props.periodB ? "B" : null].filter(Boolean);
const periodRows = computed(() => {
  const out = [];
  if (props.gran === "year") {
    for (const k of periodKeys.value) {
      const a = agg.value[k];
      out.push({ key: k, name: props.label(k, true), g: groupsOf(a.o), n: sum(a.o), note: noteOf(a.o, a.months), tags: tagsOf(k) });
    }
    return out;
  }
  for (const y of years.value) {
    const keys = periodKeys.value.filter((k) => k.startsWith(y));
    const o = Array(8).fill(0);
    const months = new Set();
    keys.forEach((k) => { agg.value[k].o.forEach((v, i) => { o[i] += v; }); agg.value[k].months.forEach((m) => months.add(m)); });
    const collapsed = !openYears.value.has(y);
    out.push({
      key: `y${y}`, year: y, isYear: true, name: `ปี ${y}`, g: groupsOf(o), n: sum(o), note: noteOf(o, months),
      tags: collapsed ? [keys.includes(props.periodA) ? "A" : null, keys.includes(props.periodB) ? "B" : null].filter(Boolean) : [],
    });
    for (const k of keys) {
      const a = agg.value[k];
      out.push({
        key: k, year: y, child: true, name: props.label(k, true).replace(/ \d{4}$/, "").replace(/\/\d{4}$/, ""),
        g: groupsOf(a.o), n: sum(a.o), note: noteOf(a.o, a.months), tags: tagsOf(k),
      });
    }
  }
  return out;
});
</script>

<style lang="scss" scoped>
.cbc-head { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 10px 20px; h6 { color: #344767; } }
.cbc-badge { padding: 4px 10px; border-radius: 6px; background: #e8fef2; color: #05602d; font-size: 12px; font-weight: 600; white-space: nowrap; }
.cbc-legend { display: flex; flex-wrap: wrap; gap: 6px 16px; font-size: 13px; color: #67748e; margin-bottom: 10px; > span { display: inline-flex; align-items: center; gap: 6px; } }
.cbc-sw { width: 10px; height: 10px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.cbc-tag {
  display: inline-grid; place-items: center; width: 24px; height: 24px; border-radius: 6px; font-size: 12px; font-weight: 700; flex-shrink: 0;
  &.a { background: #05af50; color: #fff; }
  &.b { background: #ced4da; color: #344767; }
  &.sm { width: 20px; height: 20px; font-size: 11px; margin-left: 6px; vertical-align: 1px; }
}
.cbc-context {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; padding: 10px 14px; background: #f8f9fa;
  border: 1px solid #dee2e6; border-bottom: 0; border-radius: 10px 10px 0 0; font-size: 13px; color: #67748e;
  > span { display: inline-flex; align-items: center; gap: 7px; }
  b { color: #344767; }
}
.cbc-scroll { border: 1px solid #dee2e6; border-radius: 10px; }
.cbc-context + .cbc-scroll { border-radius: 0 0 10px 10px; }
.cbc-subtitle { color: #344767; margin: 20px 0 8px; }
.cbc-periods-head { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 20px; .cbc-subtitle { margin: 0; } }
.cbc-seg { display: inline-flex; flex-wrap: wrap; gap: 6px; }
.cbc-periods { max-height: 620px; overflow: auto; }
.cbc-table {
  font-size: 0.875rem; color: #344767;
  /* data sits in the middle of its column, under the centred header; only the row labels stay left */
  th, td { padding: 8px; border-bottom: 1px solid #dee2e6; text-align: center; white-space: nowrap; vertical-align: middle; }
  thead th { position: sticky; top: 0; z-index: 3; background: #194d30; color: #fff; text-align: center; font-weight: 600; }
  thead th:first-child { text-align: left; }
  .th-main { display: block; }
  .th-sub { display: block; margin-top: 2px; font-size: 11px; font-weight: 400; opacity: 0.8; }
  tbody th { text-align: left; font-weight: 600; color: #344767; }
  td small { display: block; font-size: 11px; color: #7b809a; line-height: 1.2; }
  td.b { color: #7b809a; }
  td.d { font-weight: 600; font-size: 13px; &.flat { color: #7b809a; font-weight: 400; } }
  td.note { white-space: normal; min-width: 160px; font-size: 12px; color: #7b809a; }
  tr.sub th, tr.age th { padding-left: 30px; font-weight: 400; color: #67748e; }
  tr.age td { font-size: 13px; }
  tr.early { cursor: pointer; }
  tr.early th, tr.early td { background: #eff6ff; }
  tr.sub td { font-size: 13px; }
  tr.total th, tr.total td { background: #90f9be; font-weight: 700; }
  tr.year-row th, tr.year-row td { background: #e3feef; font-weight: 700; cursor: pointer; }
  tr.year-row:hover th, tr.year-row:hover td { background: #d1fee4; }
  tr.child th { padding-left: 38px; font-weight: 400; }
  tr.is-a th, tr.is-a td { background: #e8fef2; }
}
.cbc-rowlabel { display: inline-flex; align-items: center; gap: 6px; }
.cbc-row-toggle {
  display: inline-grid; place-items: center; width: 22px; height: 22px; margin-right: 6px; padding: 0; border: 1px solid #05af50;
  border-radius: 6px; background: #fff; color: #05af50; cursor: pointer; vertical-align: middle;
  i { font-size: 18px; transition: transform 0.15s; }
  &[aria-expanded="true"] i { transform: rotate(90deg); }
}
.cbc-row-hint { margin-left: 4px; font-size: 12px; font-weight: 400; color: #7b809a; }
.cbc-mixbar { display: flex; gap: 2px; width: 150px; height: 10px; margin: 0 auto; span { height: 100%; border-radius: 2px; min-width: 0; } }
.cbc-cellbar { display: inline-flex; align-items: center; gap: 8px; i { display: inline-block; height: 6px; border-radius: 3px; background: #05af50; opacity: 0.45; } }
.cbc-explain {
  display: flex; gap: 10px; align-items: flex-start; margin-bottom: 10px; padding: 10px 14px; border-radius: 8px;
  background: #f8f9fa; border-left: 3px solid #4f46e5; color: #67748e; font-size: 13px;
  > i { color: #4f46e5; font-size: 20px; }
  b { color: #344767; }
}
.cbc-flag {
  display: flex; gap: 8px; align-items: flex-start; margin-top: 10px; padding: 10px 14px; border-radius: 8px;
  background: #f8f9fa; border-left: 3px solid #fb8c00; color: #67748e; font-size: 13px;
  i { color: #fb8c00; font-size: 20px; }
}
</style>
